<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/utils/auth'
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

import authV2ResetPasswordIllustrationDark from '@images/pages/auth-v2-login-abastores-illustration-dark.png'
import authV2ResetPasswordIllustrationLight from '@images/pages/auth-v2-login-abastores-illustration-light.png'

definePage({
  meta: {
    layout: 'blank',
  },
})

const { t } = useI18n()

interface FormState {
  email: string
  new_password1: string
  new_password2: string
}

const form = reactive<FormState>({
  email: '',
  new_password1: '',
  new_password2: '',
})

const errors = reactive({
  email: '',
  new_password1: '',
  new_password2: '',
  apiResponse: '',
  isValid: false,
})

const successMessage = ref('')

const authThemeImg = useGenerateImageVariant(authV2ResetPasswordIllustrationLight,
  authV2ResetPasswordIllustrationDark,
)

const isNewPassword1Visible = ref(false)
const isNewPassword2Visible = ref(false)
const router = useRouter()
const authStore = useAuthStore()

const { uid, token }: { uid?: string; token?: string }
  = router.currentRoute.value.query

if (!uid || !token)
  router.push('/')

const validate = (formData): boolean => {
  errors.isValid = false
  errors.email = ''
  errors.new_password1 = ''
  errors.new_password2 = ''
  errors.apiResponse = ''

  if (!formData.email.trim()) {
    errors.email = t('You must enter an email')

    return false
  }
  else if (!/^\S[^\s@]*@\S[^\s.]*\.\S+$/.test(formData.email.trim())) {
    errors.email = t('It must be a valid email')

    return false
  }

  if (!formData.new_password1) {
    errors.new_password1 = t('You must enter a valid password')

    return false
  }

  if (formData.new_password1.length < 8) {
    errors.new_password1 = t('The password must be at least 8 characters long')

    return false
  }

  if (!formData.new_password2) {
    errors.new_password2 = t('You must enter a valid password')

    return false
  }

  if (formData.new_password1 !== form.new_password2) {
    errors.new_password2 = t('Both Passwords must be the same')

    return false
  }
  errors.isValid = true

  return true
}

const handleSubmit = async () => {
  try {
    if (!uid || !token) {
      router.push({ name: 'login' })

      return
    }

    const formData = { email: form.email.trim(), new_password1: form.new_password1, new_password2: form.new_password2 }
    const isValid = validate(formData)
    if (!isValid)
      return

    const response = await authStore.reset_password(
      formData.email,
      formData.new_password1,
      formData.new_password2,
      uid,
      token,
    )

    if (response && response.status === 200) {
      await authStore.login(formData.email, formData.new_password1)
      successMessage.value = t('Password reset successful and user logged in.')
      router.push({ name: 'dashboards-home' })
    }
    else if (response.status === 401) {
      errors.new_password1 = t('We are improving the system. Please try again later')
    }
    else if (response.data?.error) {
      const errorKey = response.data.error

      errors.new_password1 = t(errorKey)
    }
    else if (response.status >= 400 && response.status < 500) {
      const errorData = response.data

      if (response.status === 400) {
        if (errorData?.token)
          errors.apiResponse = t('The recovery link is invalid or has expired. Please request a new one.')

        else if (errorData?.new_password2)
          errors.new_password2 = t('The password entered is too common or contains only numeric characters.')

        else
          errors.apiResponse = t('There seems to be an error in the submitted data. Please check and try again.')
      }
    }
    else {
      errors.apiResponse = t('There was a problem with your request. Please try again later.')
    }
  }
  catch (error: any) {
    console.error('Error while resetting password:', error)
    errors.new_password1 = t('Unable to connect to the server. Please check your internet connection or try again later.')
    errors.isValid = false
  }
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>
  <VRow
    no-gutters
    class="auth-wrapper bg-surface"
  >
    <VCol
      md="8"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background w-100 me-0">
        <div
          class="d-flex align-center justify-center w-100 h-100"
          style="padding-inline: 150px;"
        >
          <VImg
            max-width="451"
            :src="authThemeImg"
            class="auth-illustration mt-16 mb-2"
          />
        </div>
      </div>
    </VCol>
    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-6"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            {{ t('Reset Password 🔒') }}
          </h4>
        </VCardText>
        <VCardText>
          <VForm @submit.prevent="handleSubmit">
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="form.email"
                  label="Email"
                  placeholder="example@domain.com"
                  type="email"
                  :error-message="errors.email"
                />
              </VCol>
              <VCol cols="12">
                <AppTextField
                  v-model="form.new_password1"
                  autofocus
                  :label="t('New Password')"
                  placeholder="············"
                  :type="isNewPassword1Visible ? 'text' : 'password'"
                  :append-inner-icon="isNewPassword1Visible ? 'tabler-eye-off' : 'tabler-eye'"
                  :error-message="errors.new_password1"
                  @click:append-inner="isNewPassword1Visible = !isNewPassword1Visible"
                />
              </VCol>
              <VCol cols="12">
                <AppTextField
                  v-model="form.new_password2"
                  :label="t('Confirm Password')"
                  placeholder="············"
                  :type="isNewPassword2Visible ? 'text' : 'password'"
                  :append-inner-icon="isNewPassword2Visible ? 'tabler-eye-off' : 'tabler-eye'"
                  :error-message="errors.new_password2"
                  @click:append-inner="isNewPassword2Visible = !isNewPassword2Visible"
                />
              </VCol>
              <VCol
                v-if="successMessage"
                cols="12"
              >
                <div class="success-message">
                  {{ successMessage }}
                </div>
              </VCol>
              <VCol v-if="errors.new_password1">
                <div class="error-message">
                  {{ errors.new_password1 }}
                </div>
              </VCol>
              <VCol v-if="errors.apiResponse">
                <div class="error-message">
                  {{ errors.apiResponse }}
                </div>
              </VCol>
              <VCol v-if="errors.new_password2">
                <div class="error-message">
                  {{ errors.new_password2 }}
                </div>
              </VCol>
              <VCol cols="12">
                <VBtn
                  block
                  type="submit"
                >
                  {{ t('Set New Password') }}
                </VBtn>
              </VCol>
              <VCol cols="12">
                <RouterLink
                  class="d-flex align-center justify-center"
                  :to="{ name: 'login' }"
                >
                  <VIcon
                    icon="tabler-chevron-left"
                    size="20"
                    class="me-1 flip-in-rtl"
                  />
                  <span>{{ t('Back to login') }}</span>
                </RouterLink>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";

.error-message {
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  background-color: #f8d7da;
  color: #721c24;
  margin-block-start: 8px;
  padding-block: 8px;
  padding-inline: 12px;
}

.success-message {
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  background-color: #d4edda;
  color: #155724;
  margin-block-start: 8px;
  padding-block: 8px;
  padding-inline: 12px;
}
</style>
